import React, { FC, useId } from 'react';

import {
	EvFlexColumn,
	EvFlexRow,
	EvInfoTooltip,
	EvLabelDefault,
	EvTextInput,
	TEXT_COLOR
} from '@evinced-private/ui-common';

import { REQUIRED_FIELD_INDICATOR } from './EvTextFieldWrapper.constants';
import { EvTextFieldWrapperProps } from './EvTextFieldWrapper.types';

/**
 * Wraps `EvTextInput` with a label, an optional required asterisk, and an
 * optional info tooltip. It generates the field `id` and associates it with the
 * label, and mirrors `isRequired` onto the input — so callers get an accessible,
 * required-aware field without managing ids or re-implementing the label layout.
 * Any other `EvTextInput` prop is forwarded as-is.
 */
export const EvTextFieldWrapper: FC<EvTextFieldWrapperProps> = ({
	label,
	isRequired = false,
	tooltip,
	tooltipTitle,
	...textInputProps
}) => {
	const fieldId = useId();

	return (
		<EvFlexColumn gap="SMALL">
			<EvFlexRow align="CENTER" gap="SMALL">
				<EvLabelDefault as="label" htmlFor={fieldId}>
					<EvFlexRow as="span" gap="SMALL" align="CENTER" width="fit-content">
						{label}
						{isRequired && (
							<EvLabelDefault as="span" color={TEXT_COLOR.RED} aria-hidden>
								{REQUIRED_FIELD_INDICATOR}
							</EvLabelDefault>
						)}
					</EvFlexRow>
				</EvLabelDefault>
				{tooltip && <EvInfoTooltip title={tooltipTitle ?? label}>{tooltip}</EvInfoTooltip>}
			</EvFlexRow>
			<EvTextInput {...textInputProps} id={fieldId} isRequired={isRequired} />
		</EvFlexColumn>
	);
};
